import authService from '@/services/auth.service'
import { UserRolesNames } from '@/services/organization-user/constants'
import { deleteCookie, getCookie, setCookie, updateTitleLanguage } from '@/services/utils'
import store from '@/store'
import { checkPermission } from '@/store/permissions'
import { createRouter, createWebHistory } from 'vue-router'
import {
  accountsRoutes,
  dashboardRoutes,
  domainsRoutes,
  externalRoutes,
  inventoryRoutes,
  libraryRoutes,
  playlistsRoutes,
  accountRoutes,
  myFeedRoutes,
  widgetsRoutes
} from './routes'
import type { RouteRecordRaw } from 'vue-router'
import { CRUD, DataType } from '@/interfaces'
const routes: Array<RouteRecordRaw> = [
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: {
      title: 'register',
      hideProcessingPanel: true
    }
  },
  {
    path: '/ui',
    name: 'ui',
    component: () => import('@/views/UI_elements.vue')
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/Maintenance.vue'),
    meta: {
      hideProcessingPanel: true
    }
  },
  {
    path: '/signup-complete',
    name: 'signupcomplete',
    component: () => import('@/views/SignupComplete.vue'),
    meta: {
      title: 'sign up complete',
      hideProcessingPanel: true
    }
  },
  {
    path: '/layout',
    name: 'layout',
    component: () => import('@/views/Layout.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: 'login',
      hideProcessingPanel: true
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/password/ForgotPassword.vue'),
    meta: {
      title: 'reset-password',
      hideProcessingPanel: true
    }
  },
  {
    path: '/reset-password',
    name: 'resetpassword',
    component: () => import('@/views/password/ResetPassword.vue'),
    meta: {
      title: 'reset-password',
      hideProcessingPanel: true
    }
  },
  {
    path: '/org-auth',
    name: 'org-auth',
    component: {
      template: '<div />'
    },
    meta: {
      hideProcessingPanel: true
    },
    beforeEnter: async (to, from, next) => {
      const { organizationId, userId, rs } = to.query as { organizationId: string; userId: string; rs: string }
      if (organizationId && userId && rs) {
        await authService.loginFromAuth(Number(organizationId), Number(userId), rs)
      }
      // todo redirect to 404 if token expired or not available
      next({ name: 'home' })
    }
  },
  {
    path: '/auth/sso',
    name: 'auth-sso',
    component: {
      template: '<div />'
    },
    meta: {
      hideProcessingPanel: true
    },
    beforeEnter: async (to, from, next) => {
      const { code, playlist } = to.query as { code: string; playlist?: string }
      if (code) {
        try {
          await authService.loginFromSSO(code)
          if (playlist) {
            next({ name: 'editplaylist', params: { id: playlist }, query: { embed: 'false' } })
          } else {
            next({ name: 'home' })
          }
        } catch (error) {
          next({ name: 'login' })
        }
      } else {
        next({ name: 'login' })
      }
    }
  },
  {
    path: '/',
    name: 'home',
    redirect: () => {
      if (!store.state.isAuthenticated) {
        return '/login'
      }
      return '/dashboard/'
    },
    component: () => import('@/views/Home.vue'),
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardPage.vue'),
        meta: {
          title: 'dashboard',
          navbar: 'dashboard'
        },
        redirect: () => {
          if (
            store.getters['user/isRole'](UserRolesNames.DemandOwner) ||
            store.getters['user/isRole'](UserRolesNames.DemandAccounting) | store.getters['user/isRole'](UserRolesNames.DemandEditor)
          ) {
            return '/maintenance'
          }
          if (checkPermission('publisher_dashboard')) {
            return '/dashboard/publisher-dashboard'
          } else if (checkPermission('content_dashboard')) {
            return '/dashboard/content-dashboard'
          } else if (checkPermission('agency_dashboard')) {
            return '/dashboard/agency-dashboard'
          } else if (store.getters['user/isRole'](UserRolesNames.ContentEditor)) {
            return '/videos/videos'
          } else if (store.getters['user/isRole'](UserRolesNames.PublisherEditor)) {
            return '/library'
          } else if ([UserRolesNames.ContentAccounting, UserRolesNames.PublisherAccounting].some((r) => store.getters['user/isRole'](r))) {
            return '/reports'
          } else {
            return '/login'
          }
        },
        beforeEnter: () => {
          if (import.meta.env.VITE_APP_IS_PRODUCTION === 'true') {
            const userFullName = store.state.user.user.first_name + ' ' + store.state.user.user.last_name
            heap.identify(userFullName)
            heap.addUserProperties({ email: store.state.user.user.email, organization: store.state.user.user.organization_id })
            hj('identify', store.state.user.user.id, {
              email: store.state.user.user.email,
              organization: store.state.user.user.organization_id
            })
            mixpanel.identify(store.state.user.user.id) // Use unique user ID for identification
            mixpanel.people.set({
              $first_name: store.state.user.user.first_name,
              $last_name: store.state.user.user.last_name,
              $name: userFullName,
              $email: store.state.user.user.email,
              organization: store.state.user.user.organization_id
            })
          }
        },
        children: dashboardRoutes
      },
      {
        path: 'inventory',
        name: 'inventory',
        redirect: () => {
          if (!checkPermission('inventory_my_domains') && checkPermission('inventory_excluded_domains')) {
            return '/inventory/excluded-domains'
          } else {
            return '/inventory/publisher-domains'
          }
        },
        component: () => import('@/views/inventory/InventoryPage.vue'),
        children: inventoryRoutes,
        meta: {
          title: 'inventory',
          navbar: 'inventory'
        },
        beforeEnter: (to, from, next) => checkPermission('inventory', next)
      },
      {
        path: 'videos',
        name: 'my-videos',
        redirect: '/videos/videos',
        component: () => import('@/views/myfeed/MyFeedPage.vue'),
        children: myFeedRoutes,
        meta: {
          title: 'my-videos',
          navbar: 'my-feed'
        },
        beforeEnter: (to, from, next) => checkPermission('videos', next)
      },
      {
        path: 'ai-generated',
        name: 'ai-generated',
        redirect: { name: 'pending-ai-generated' },
        component: () => import('@/views/ai-generated/AiGenerated.vue'),
        meta: {
          title: 'ai-generated',
          navbar: 'ai-generated'
        },
        children: [
          {
            path: 'edit/:id',
            name: 'ai-generated-video-edit',
            component: () => import('@/components/content/ai-generated/EditAiGeneratedVideoSettings.vue'),
            meta: {
              title: 'ai-generated',
              fullscreen: true,
              hideProcessingPanel: true
            }
          },
          {
            path: 'create',
            name: 'ai-generated-video-create',
            component: () => import('@/components/content/ai-generated/AiVideoCreation.vue'),
            meta: {
              title: 'ai-generated-video-creation',
              fullscreen: true,
            }
          },
          {
            path: 'pending',
            name: 'pending-ai-generated',
            component: () => import('@/components/content/videos/PendingAiGenerated.vue'),
            meta: {
              title: 'pending-ai-generated'
            }
          },
          {
            path: 'approved',
            name: 'approved-ai-generated',
            component: { render: () => null },
            meta: {
              title: 'ai-generated',
              navbar: 'ai-generated'
            }
          }
        ],
        beforeEnter: (to, from, next) => checkPermission('ai_generated', next)
      },
      {
        path: 'library',
        name: 'library',
        component: () => import('@/views/content/LibraryPage.vue'),
        meta: {
          title: 'library',
          navbar: 'library'
        },
        children: libraryRoutes
        // beforeEnter: (to, from, next) => checkPermission(to.path, next),
      },
      {
        path: 'packages',
        name: 'packages',
        component: () => import('@/views/myfeed/PackagesPage.vue'),
        meta: {
          title: 'packages',
          navbar: 'packages'
        },
        children: [
          {
            path: 'add',
            name: 'add-package',
            component: () => import('@/components/content/packages/my-packages/addpackage/PackageWizard.vue'),
            beforeEnter: (to, from, next) => checkPermission('my_packages_packages', next),
            meta: {
              title: 'packages',
              fullscreen: true,
              hideProcessingPanel: true
            }
          },
          {
            path: 'duplicate/:id',
            name: 'duplicate-package',
            component: () => import('@/components/modals/custom-confirmation/CustomConfirmation.vue'),
            beforeEnter: (to, from, next) => checkPermission('my_packages_packages', next),
            meta: {
              title: 'packages',
              modal: true
            },
            props: { type: DataType.Package, action: CRUD.Duplicate }
          },
          {
            path: 'edit/:id',
            name: 'edit-package',
            component: () => import('@/components/content/packages/my-packages/addpackage/PackageWizard.vue'),
            props: true,
            beforeEnter: (to, from, next) => checkPermission('my_packages_packages', next),
            meta: {
              title: 'packages',
              fullscreen: true,
              hideProcessingPanel: true
            }
          },
          {
            path: 'accept-proposals',
            name: 'pd-accept-proposals',
            component: () => import('@/components/content/packages/AcceptAllProposals.vue'),
            props: true,
            meta: {
              title: 'packages'
            }
          },
          {
            //temp just to fix novu redirect cta will be deleted in the future
            path: 'packages',
            name: 'packages-packages-old-route',
            redirect: () => {
              return { name: 'packages' }
            }
          },
          {
            //temp just to fix novu redirect cta will be deleted in the future
            path: 'offered-packages/edit/:id',
            name: 'packages‑offered‑packages‑old‑route',
            redirect: (to) => {
              return { name: 'ext-edit-package', params: to.params }
            }
          }
        ]
      },
      {
        path: 'offered-packages',
        name: 'offered-packages',
        component: () => import('@/views/content/OfferedPackagesPage.vue'),
        beforeEnter: (to, from, next) => checkPermission('packages_offered_packages', next),
        meta: {
          title: 'offered-packages',
          navbar: 'offered-packages'
        },
        children: [
          {
            path: 'accept-proposals',
            name: 'ext-accept-proposals',
            component: () => import('@/components/content/packages/AcceptAllProposals.vue'),
            props: true,
            meta: {
              title: 'content'
            }
          },
          {
            path: 'edit/:id',
            name: 'ext-edit-package',
            component: () => import('@/components/content/packages/offered-packages/OfferedPackagesEdit.vue'),
            props: true,
            meta: {
              title: 'packages',
              hideProcessingPanel: true
            },
            children: [
              {
                path: 'closeunsaved',
                name: 'edit-closeUnsavedOfferedPackages',
                component: () => import('@/components/modals/custom-confirmation/CustomConfirmation.vue'),
                meta: {
                  title: 'packages'
                },
                props: { type: DataType.OfferedPackages, action: CRUD.Confirm }
              }
            ]
          },
          {
            path: 'closeunsaved',
            name: 'edit-closeUnsavedOfferedPackages',
            component: () => import('@/components/modals/custom-confirmation/CustomConfirmation.vue'),
            meta: {
              title: 'packages'
            },
            props: { type: DataType.OfferedPackages, action: CRUD.Confirm }
          }
        ]
      },
      {
        path: 'playlists',
        name: 'playlists',
        props: true,
        component: () => import('@/views/player/PlaylistPage.vue'),
        meta: {
          title: 'playlists',
          navbar: 'playlists'
        },
        children: playlistsRoutes
      },
      {
        path: 'widgets',
        name: 'widgets',
        component: () => import('@/views/player/WidgetPage.vue'),
        beforeEnter: (to, from, next) => checkPermission(to.path, next),
        meta: {
          title: 'widgets',
          navbar: 'widgets'
        },
        children: widgetsRoutes
      },

      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/reports/ReportsPage.vue'),
        beforeEnter: (to, from, next) => checkPermission('reports', next),
        meta: {
          title: 'reports',
          navbar: 'reports'
        }
      },

      {
        path: 'account',
        name: 'account',
        component: () => import('@/views/settings/SettingsPage.vue'),
        meta: {
          title: 'account',
          navbar: 'account'
        },
        children: accountRoutes
      },
      {
        path: 'domains-inventory',
        name: 'domains-inventory',
        component: () => import('@/views/domains/AgencyDomainsPage.vue'),
        meta: {
          title: 'domains-inventory',
          navbar: 'domains-inventory'
        },
        children: domainsRoutes,
        beforeEnter: (to, from, next) => checkPermission(to.path, next)
      },
      {
        path: 'accounts',
        name: 'accounts',
        component: () => import('@/views/accounts/AccountsPage.vue'),
        meta: {
          title: 'accounts',
          navbar: 'accounts'
        },
        children: accountsRoutes,
        beforeEnter: (to, from, next) => checkPermission(to.path, next)
      }
    ]
  },
  { path: '/:pathMatch(.*)*', redirect: '/' }
]
const router = createRouter({
  history: createWebHistory(),
  routes
})
let isPageRefreshed = false

export const nonAuthRoutes = externalRoutes.map((r) => r.path)
// export const nonAuthRoutes = ['login', 'register', 'password', 'ui', 'layout']

router.beforeEach(async (to, from) => {
  isPageRefreshed = !from.name
  if (import.meta.env.VITE_APP_MAINTENANCE_MODE === 'true') {
    if (to.name !== 'maintenance') {
      return { name: 'maintenance' }
    } else {
      return
    }
  }

  if (to.name === 'org-auth' || to.name === 'auth-sso') {
    deleteCookie('lastLoginTime')
    await authService.logout(false)
    localStorage.removeItem('vuex')
    return
  }

  updateTitleLanguage(to.meta.title)

  if (!store.state.isAuthenticated && window.history.state.back === null && !nonAuthRoutes.some((r) => to.fullPath.includes(r))) {
    store.commit('setRedirectRoute', window.history.state.current)
  }
  // If the route is the login page, don't perform authentication check
  if (to.name === 'login') {
    return
  }

  if (!store.state.isAuthenticated && !nonAuthRoutes.some((r) => to.fullPath.includes(r))) {
    return { name: 'login' }
  }

  // Check the expiration date of the cookie
  const lastLoginTime = getCookie('lastLoginTime')
  if (lastLoginTime) {
    const twoDaysAgo = new Date()
    const twoDays = 2
    twoDaysAgo.setDate(twoDaysAgo.getDate() - twoDays)

    if (new Date(lastLoginTime) <= twoDaysAgo) {
      // Logout the user if the cookie is 2 days old
      deleteCookie('lastLoginTime')
      await authService.logout()
      return
    }
  } else {
    // If the cookie doesn't exist and the user is authenticated, create a new one
    if (store.state.isAuthenticated) {
      const now = new Date()
      setCookie('lastLoginTime', now.toUTCString())
    }
  }
})

export function getIsPageRefreshed(): boolean {
  return isPageRefreshed
}

export default router
